import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useMutation } from '@tanstack/react-query';
import { useGetSubdomainsQuery } from 'app/data/queries/use-get-subdomains-query';
import { NavigationCommandData } from 'app/data/types/content';
import { useApi } from 'app/shared/contexts/api';
import { formatSubdomain } from 'app/shared/utils/format-subdomain';
import { useBuilding } from 'app/shared/contexts/building';
import { useContent } from 'app/shared/contexts/content';

export const getContentDeepLinkMutationKey = (contentUuid: string, subDomain: string): QueryKey => ['content-deep-link', contentUuid, subDomain];

interface GetContentDeepLinkResponse {
  url: string;
}

export const useGetContentDeepLinkMutation = () => {
  const { apiUrl, getAuthHeaders } = useApi();
  const { data: subdomains, isLoading: isSubdomainsLoading } = useGetSubdomainsQuery();
  const { building } = useBuilding();
  const buildingUuid = building?.uuid ?? '';
  const { content } = useContent();
  const contentUuid = content?.uuid ?? '';
  const navigationCommand = content?.navigation_command ?? undefined;

  const isReady = !isSubdomainsLoading;

  const mutation = useMutation({
    mutationKey: getContentDeepLinkMutationKey(contentUuid, subdomains?.[0]?.subdomain ?? ''),
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const body: Record<string, unknown> = {};

      if (navigationCommand && Object.keys(navigationCommand).length > 0) {
        body.navigation_command = navigationCommand;
      }

      if (subdomains?.[0]?.subdomain) {
        body.default_redirect = formatSubdomain(subdomains[0].subdomain);
      }

      const response = await apiRequest<GetContentDeepLinkResponse, { navigation_command?: NavigationCommandData }, void>({
        url: `${apiUrl}/api/admin/v2/buildings/${buildingUuid}/content/${contentUuid}/deeplink`,
        method: HttpMethod.POST,
        body,
        headers,
      });

      return response.isEmpty() ? { url: '' } : response.data;
    },
    retry: false,
  });

  return { ...mutation, isReady };
};
